import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { NoBubblesEventInit } from "@cervval.js/common/src/tools/event.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import "tinymce/skins/ui/oxide/content.inline.min.css";
import "tinymce/skins/ui/oxide/skin.min.css";
import tinymce, { Editor as EditorTiny, Events, RawEditorOptions } from "tinymce/tinymce";
import { CjsEditor, EditorOnChangeEvent, EditorOptions } from "../editor";
import { tinyMceContentCss, tinyMceOxideSkinCss, tinyMceOxideUiCss } from "./tinyMcePlugins";

export type TinyMceConfiguration = Partial<RawEditorOptions>;
export type BaseTinyMceConfigurationsKey = "basic" | "minimal" | "advanced" | "advancedToolbar";
export type BaseTinyMceConfigurations = { [key in BaseTinyMceConfigurationsKey]: TinyMceConfiguration }

export const tinyMceBaseConfig: RawEditorOptions = {
    height: 200,
    target: undefined,
    menubar: false,
    statusbar: false,
    elementpath: false,
    skin: false,
    content_css: false,
    content_style: tinyMceOxideSkinCss + "\n" + tinyMceOxideUiCss + "\n" + tinyMceContentCss,
    theme: "silver",
    toolbar_mode: "sliding",
    browser_spellcheck: true,
    branding: false,
    promotion: false
};

export const BaseTinyMceConfigurationsValues: BaseTinyMceConfigurations = {
    // The basic TinyMCE editor
    basic: tinyMceBaseConfig,
    // A text editor with the minimal text edition requirements
    minimal: {
        ...tinyMceBaseConfig,
        formatselect: false,
        toolbar: "bold italic underline"
    },
    advanced: {
        ...tinyMceBaseConfig,
        menubar: true,
        plugins: ["image", "link", "autoresize"],
        toolbar: "bold italic underline forecolor link image"
    },
    advancedToolbar: {
        ...tinyMceBaseConfig,
        toolbar: "undo redo | bold italic underline forecolor | fontsize | alignleft aligncenter alignright alignjustify"
    }
};

export interface TinyMceEditorOptions extends EditorOptions {

    /** Configuration of TinyMCeEditor */
    configuration: TinyMceConfiguration | BaseTinyMceConfigurationsKey;

    /** Placeholder of tinyMce Editor */
    placeholder?: ValueOrFunction<string>;

    /** Arialabel of tinyMce Editor */
    ariaLabel?: string;

    /** Disable placeholder */
    disabled: boolean;

    /** set callback to handle images in editor 
     * images are by default set as blob in <img> and editor is saved in BDD as text
     */
    imagePicker?: {
        enable: boolean,
        advanced: boolean,
        imageUploadHandler: (image: File) => void
    };

    /** Change the behavior of the keys to go to the new line */
    newLineBehavior?: "block" | "linebreak" | "invert" | "default";
}

export const defaultTinyMceEditorOptions: TinyMceEditorOptions = {
    configuration: "basic",
    disabled: false,
    multipleValuesState: false
};

@CustomElement({
    name: "cjs-tinymce-editor",
    style: require("./tinyMceEditor.scss"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO],
    doNotRefreshAfterInit: true
})
export class CjsTinyMceEditor extends CjsEditor<string> {

    /** 
     * Triggered when input of editor has changed
     */
    @Event(NoBubblesEventInit)
    public inputEvent!: EventHandler<EditorOnChangeEvent<string>>;

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultTinyMceEditorOptions)
    public readonly options!: WebComponentOptions<TinyMceEditorOptions>;

    /** Configuration of TinyMceEditor */
    public get configuration(): TinyMceConfiguration {
        const configOption = this.options.configuration;

        let config: TinyMceConfiguration;
        if (typeof configOption === "string") {// Base config key
            config = BaseTinyMceConfigurationsValues[configOption];
        } else {// Custom config
            config = configOption;
        }

        // add an element to the config, if the newLineBehavior is called
        config.newline_behavior = this.options.newLineBehavior ?? "default";

        // if image picker is enable,use this default configs
        if (this.options.imagePicker?.enable) {
            const opts = this.options;
            config = {
                ...config,
                image_advtab: this.options.imagePicker?.advanced ?? false,
                block_unsupported_drop: false,
                file_picker_types: "image",
                automatic_uploads: true,
                elementpath: false,
                image_title: true,
                file_picker_callback: function (cb: any, value: any, meta: any) {
                    // ths could be replace by the cjs filePicker with some modifications
                    const input = document.createElement("input");
                    input.setAttribute("type", "file");
                    input.setAttribute("accept", "image/*");
                    // Note: In modern browsers input[type="file"] is functional without 
                    // even adding it to the DOM, but that might not be the case in some older
                    // or quirky browsers like IE, so you might want to add it to the DOM
                    // just in case, and visually hide it. And do not forget do remove it
                    // once you do not need it anymore.
                    input.onchange = function () {
                        const file = (<any>this).files[0];
                        const reader = new FileReader();
                        reader.onload = async function () {
                            // Note: Now we need to register the blob in TinyMCEs image blob
                            // registry. In the next release this part hopefully won't be
                            // necessary, as we are looking to handle it internally.
                            const id = "blobid" + new Date().getTime();
                            const blobCache = tinymce?.activeEditor?.editorUpload.blobCache;
                            if (blobCache) {
                                const blobInfo = blobCache.create(id, file, String(reader.result), file.name.substring(0, file.name.lastIndexOf(".")));
                                blobCache.add(blobInfo);
                                // call the callback and populate the Title field with the file name
                                // the callback will fire the images_upload_handler
                                cb(blobInfo.blobUri(), { title: file.name });
                            }
                        };
                        reader.readAsDataURL(file);
                    };
                    input.click();
                },
                images_upload_handler: async (blobInfo: any, progress) => {
                    // on save click in filePicker we call the callback and get the return value to setup the source of the img beacon
                    if (blobInfo.blob) {
                        return Promise.resolve(await
                            opts.imagePicker?.imageUploadHandler(new File([blobInfo.blob()], blobInfo.filename())
                            ));
                    }
                    return Promise.resolve(blobInfo.blobUri());
                }
            };
        }
        return config;
    }

    /** Textarea where tinyMce editor will be load */
    public tinyMceEditorContainer?: HTMLElement;

    /** The tinyMceEditor (from tinymce lib) */
    public tinyEditor?: EditorTiny;

    /** Callback to define; called after TinyMceComponent is loaded */
    public afterTinyMceEditorLoad?: () => void = undefined;

    /** TinyMceEditor - editor is loaded */
    public isLoaded = false;

    constructor(options?: Partial<TinyMceEditorOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * When component is loaded
     */
    public override init(): void {
        super.init();

        this.tinyMceEditorContainer = createHtmlElement(this.configuration.inline ? "div" : "textarea", {// TinyMCE's inline mode does not work on <textarea> elements
            attributes: {
                id: "tinyMceEditorTextarea" + this.componentNumber
            }
        });

        // Add textarea to component
        this.append(this.tinyMceEditorContainer);

        /**
         * FIXME - AMT I don't known why setTimeout is required here to work properly
         * (Surement dû au load du tinyMce qui est asynchrone, l'await ne fonctionne pas non plus)
         */
        setTimeout(() => {
            this._loadTinyMceEditor().then(() => {
                // Ensure that tiny mce has been loaded before the refresh, else the element doesn't exit in the DOM
                this.refresh();
            }).catch(getLogConsole().error);

        });
    }

    /** Refresh component */
    public override refresh(): void {
        super.refresh();
        // Toggle multiple values class on iframe element
        this._toggleMultipleValuesClass(this.querySelector("iframe"));
    }


    /** Get tiny editor */
    public getInput(): HTMLElement {
        return this.tinyEditor ? this.tinyEditor.editorContainer : document.createElement("div");
    }

    /**
     * Set a custom configuration for TinyMceEditor, editor is refresh if config has changed
     */
    public setCustomConfiguration(customConfiguration: TinyMceConfiguration): void {
        this.options.setAndRefreshIfChanged({
            configuration: customConfiguration
        });
    }

    /**
     * load TinyMceEditor and set its text from this._value
     */
    private _loadTinyMceEditor(): Promise<void> {
        return this._initTinymce().then((editors: EditorTiny[]) => {

            this.tinyEditor = editors[0];
            if (this.tinyEditor) {
                this.tinyEditor.setContent(this._value ?? "");

                // Set tinyMceEditor width 100%
                this.querySelector("[role=\"application\"]")?.classList.add("w-100");
                // Add an aria-label
                if (this.tinyMceEditorContainer) {
                    this.tinyMceEditorContainer.ariaLabel = this.options.ariaLabel ?? "";
                }
                // Dispatch 'has load' event for tinymce editor
                this.isLoaded = true;
                if (this.afterTinyMceEditorLoad) {
                    this.afterTinyMceEditorLoad();
                }

                // Subscribe to value changes
                this.tinyEditor.on("change", (e: Events.ChangeEvent) => {
                    this._onInputValueChange(e);
                });


                // Subscribe to value changes
                this.tinyEditor.on("input", (e) => {
                    this.inputEvent.emit({ value: this.tinyEditor?.getContent() ?? "" });
                });

                this._refreshDisabled();
            } else {
                this._log?.error("_initTinymce failed");
            }
        });
    }

    /**
     * Init tinyMceEditor with configuration
     */
    private _initTinymce(): Promise<EditorTiny[]> {
        // Retrieve config
        const config = { ...this.configuration };
        config.target = config.target ?? this.tinyMceEditorContainer;
        config.placeholder = getVOFValue(this.options.placeholder) ?? config.placeholder;

        return tinymce.init(config);
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        this.tinyEditor?.setContent(this._value != null ? this._value : "");
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): string | undefined {
        return this.tinyEditor?.getContent();
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.tinyEditor?.mode.set(this.disabled ? "readonly" : "design");
    }
}